import { Suspense, useCallback, useRef } from 'react';
import type { WindowState } from '@/stores/windowStore';
import { useWindowStore } from '@/stores/windowStore';
import type { DesktopApp } from '@/config/desktopApps';
import { LoadingSuspense } from '@/components/ui/LoadingSuspense';

interface WindowProps {
  window: WindowState;
  app: DesktopApp;
  isActive: boolean;
}

const MIN_W = 320;
const MIN_H = 240;
const TASKBAR_H = 40;

export function Window({ window, app, isActive }: WindowProps) {
  const { closeWindow, minimizeWindow, maximizeWindow, focusWindow, moveWindow, resizeWindow } = useWindowStore();
  const Component = app.component;

  // Refs for drag
  const dragRef = useRef<{ startX: number; startY: number; startWX: number; startWY: number } | null>(null);
  // Refs for resize
  const resizeRef = useRef<{
    edge: string;
    startX: number; startY: number;
    startW: number; startH: number;
    startWX: number; startWY: number;
  } | null>(null);

  // ── Drag (title bar) ──
  const onDragMouseDown = useCallback((e: React.MouseEvent) => {
    if (window.isMaximized) return;
    e.preventDefault();
    focusWindow(window.id);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startWX: window.x, startWY: window.y };

    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      const newX = dragRef.current.startWX + dx;
      const newY = dragRef.current.startWY + dy;
      // Clamp: keep titlebar always reachable
      const clampedX = Math.max(-window.width + 80, Math.min(newX, globalThis.innerWidth - 80));
      const clampedY = Math.max(0, Math.min(newY, globalThis.innerHeight - TASKBAR_H - 20));
      moveWindow(window.id, clampedX, clampedY);
    };
    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [window.isMaximized, window.id, window.x, window.y, window.width, focusWindow, moveWindow]);

  // ── Resize (8 edges) ──
  const onResizeMouseDown = useCallback((e: React.MouseEvent, edge: string) => {
    if (window.isMaximized) return;
    e.preventDefault();
    e.stopPropagation();
    focusWindow(window.id);
    resizeRef.current = {
      edge,
      startX: e.clientX, startY: e.clientY,
      startW: window.width, startH: window.height,
      startWX: window.x, startWY: window.y,
    };

    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const { edge, startX, startY, startW, startH, startWX, startWY } = resizeRef.current;
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;

      let newW = startW, newH = startH, newX = startWX, newY = startWY;

      if (edge.includes('e'))  newW = Math.max(MIN_W, startW + dx);
      if (edge.includes('s'))  newH = Math.max(MIN_H, startH + dy);
      if (edge.includes('w')) { newW = Math.max(MIN_W, startW - dx); newX = startWX + (startW - newW); }
      if (edge.includes('n')) { newH = Math.max(MIN_H, startH - dy); newY = startWY + (startH - newH); }

      resizeWindow(window.id, newW, newH, newX, newY);
    };
    const onUp = () => {
      resizeRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [window.isMaximized, window.id, window.x, window.y, window.width, window.height, focusWindow, resizeWindow]);

  if (!Component || window.isMinimized) return null;

  const isMax = window.isMaximized;

  const style: React.CSSProperties = isMax
    ? { position: 'fixed', top: 0, left: 0, width: '100vw', height: `calc(100vh - ${TASKBAR_H}px)`, zIndex: window.zIndex }
    : { position: 'fixed', top: window.y, left: window.x, width: window.width, height: window.height, zIndex: window.zIndex };

  // Resize handle cursor map
  const handles: { edge: string; style: React.CSSProperties }[] = [
    { edge: 'n',  style: { top: 0,    left: 4,   right: 4,  height: 5, cursor: 'n-resize'  } },
    { edge: 's',  style: { bottom: 0, left: 4,   right: 4,  height: 5, cursor: 's-resize'  } },
    { edge: 'w',  style: { left: 0,   top: 4,    bottom: 4, width: 5,  cursor: 'w-resize'  } },
    { edge: 'e',  style: { right: 0,  top: 4,    bottom: 4, width: 5,  cursor: 'e-resize'  } },
    { edge: 'nw', style: { top: 0,    left: 0,   width: 10, height: 10, cursor: 'nw-resize' } },
    { edge: 'ne', style: { top: 0,    right: 0,  width: 10, height: 10, cursor: 'ne-resize' } },
    { edge: 'sw', style: { bottom: 0, left: 0,   width: 10, height: 10, cursor: 'sw-resize' } },
    { edge: 'se', style: { bottom: 0, right: 0,  width: 10, height: 10, cursor: 'se-resize' } },
  ];

  return (
    <div
      className={`win98-window flex flex-col ${isActive ? 'window-focused' : 'window-unfocused'}`}
      style={style}
      onMouseDown={() => { if (!isActive) focusWindow(window.id); }}
    >
      {/* Resize handles (hidden when maximized) */}
      {!isMax && handles.map(h => (
        <div
          key={h.edge}
          style={{ position: 'absolute', zIndex: 10, ...h.style }}
          onMouseDown={e => onResizeMouseDown(e, h.edge)}
        />
      ))}

      {/* Title Bar — drag handle */}
      <div
        className="win98-titlebar flex items-center justify-between px-1 h-8 select-none shrink-0"
        onMouseDown={onDragMouseDown}
        style={{ cursor: isMax ? 'default' : 'move' }}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <span className="shrink-0">{app.icon}</span>
          <span className="font-bold text-sm truncate">{window.title}</span>
        </div>
        <div className="flex gap-1 shrink-0" onMouseDown={e => e.stopPropagation()}>
          <button
            onClick={() => minimizeWindow(window.id)}
            className="win98-button w-6 h-6 flex items-center justify-center text-xs font-bold"
            title="Minimise"
          >_</button>
          <button
            onClick={() => maximizeWindow(window.id)}
            className="win98-button w-6 h-6 flex items-center justify-center text-xs font-bold"
            title={isMax ? 'Restore' : 'Maximise'}
          >{isMax ? '❐' : '□'}</button>
          <button
            onClick={() => closeWindow(window.id)}
            className="win98-button w-6 h-6 flex items-center justify-center text-xs font-bold"
            title="Close"
          >✕</button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden bg-[#c0c0c0]">
        <Suspense fallback={<LoadingSuspense />}>
          <Component />
        </Suspense>
      </div>
    </div>
  );
}
